import React, { useState } from "react";
import { useHistory, Link } from "react-router-dom";
import config from "../config";
import { instance } from "../instance";
import { setAccessToken } from "../apiHelpers";
import useUserStore, { userFromApi } from "../stores/useUserStore";
import useFleetStore from "../stores/useFleetStore";
import usePartsStore from "../stores/usePartsStore";
import { buttonClass } from '../components/ui/Button';
import { Field } from "../components/ui/Field";
import { Alert } from "../components/ui/Alert";
import Reveal from "../components/ui/Reveal";

// `onAuthenticated` (optional): called after a successful login INSTEAD of the
// default redirect to "/". The /pilot-auth popup passes it so login keeps the
// window on the handoff route rather than navigating to the dashboard.
const Login = ({ onAuthenticated } = {}) => {
    const [email, setEmail] = useState("");
    const [pass, setPassword] = useState("");
    const [error, setError] = useState(null);

    // Zustand stores
    const setUser = useUserStore((state) => state.setUser);
    const refreshFleet = useFleetStore((state) => state.refreshFleet);
    const refreshParts = usePartsStore((state) => state.refreshParts);

    // For navigating after login
    const history = useHistory();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        try {
            // Use JWT login endpoint
            const response = await fetch(`${config.API_ENDPOINT}/login/jwt`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: 'include',  // Receive refresh token cookie
                body: JSON.stringify({ email, pass }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Login failed. Please try again.");
            }

            const res = await response.json();

            // Store access token in memory (secure!)
            setAccessToken(res.accessToken);


            // The API's payload, normalized in one place. This used to hand-copy
            // the flags and dropped four of them, so a bookkeeper who signed in
            // could not open Invoices until she reloaded onto the refresh path.
            setUser(userFromApi(res.user));

            // Fetch app data
            refreshFleet();
            refreshParts();

            // Then navigate — unless the caller wants to stay put (e.g. the
            // /pilot-auth popup, which advances to its consent step instead).
            if (onAuthenticated) {
                onAuthenticated();
            } else {
                history.push("/");
            }
        } catch (err) {
            console.error("Login error:", err);
            setError(err.message);
        }
    };

    return (
        <div className="flex items-center justify-center mt-10 bg-[#191919]"
            style={{ fontFamily: "RobotoMono, sans-serif" }}
        >
            <form
                onSubmit={handleSubmit}
                className="login-card-glow w-11/12 max-w-md p-6 bg-[#2a2a2a] text-white
                   rounded-lg shadow-lg border-2 border-black"
            >
                {/* Instance lockup. `instance.logo` is picked at build time, so
                    this is the Firefleet mark on one deployment and the Swarm one
                    on the other with no branching here. Both configured files are
                    the near-white variants, which is what this dark card needs. */}
                <img
                    src={instance.logo}
                    alt={instance.logoAlt}
                    className="h-12 w-auto max-w-full mx-auto mb-4 object-contain"
                />

                {/* Brand rule. `primary` is the one token that switches per
                    instance (tailwind.config.js), so this is gold on Firefleet
                    and muted blue on SwarmSystems with no branching. */}
                <div className="mx-auto mb-4 h-0.5 w-12 rounded-full bg-primary" />

                <h3 className="text-center text-lg font-bold mb-6">
                    Welcome to {instance.displayName}!
                </h3>

                <Reveal show={!!error}>
                    <Alert className="mb-4">{error}</Alert>
                </Reveal>

                <div className="mb-4">
                    <label htmlFor="email" className="block mb-2">
                        Email:
                    </label>
                    <Field
                        size="lg"
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </div>

                <div className="mb-4">
                    <label htmlFor="password" className="block mb-2">
                        Password:
                    </label>
                    <Field
                        size="lg"
                        type="password"
                        id="password"
                        value={pass}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                </div>

                <button
                    type="submit"
                    className={`${buttonClass('filled', 'lg')} w-full`}
                >
                    Login
                </button>
                {/* No Register link: accounts are invitation-only now, and /register
                    is reachable only with a valid ?invite= token. Offering a link
                    that leads to "you need an invitation" is a dead end. */}
                <div className='flex flex-row justify-end mt-5'>
                    <Link
                        to='/resetpass'
                        className='text-primary hover:underline focus:outline-none
                           focus-visible:ring-2 focus-visible:ring-primary rounded'
                    >
                        Recovery
                    </Link>
                </div>
            </form>
        </div>
    );
};

export default Login;
